<template>
  <section class="container pb-8">
    <!-- Header -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="exam-header bg-green">
          <div class="header-left">
            <h4 class="exam-title">
              考試名稱：{{ PaperDetail.essayPapersName || '申論試卷' }} - 成績檢視
            </h4>
          </div>
          <div class="header-right">
            <div class="score-badge">
              <span class="label">總分：</span>
              <span class="score">{{ PaperDetail.totalScore || '--' }}</span>
              <span class="divider">/</span>
              <span class="full-score">{{ FullScore || '--' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Bar -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="status-bar">
          <div class="status-row">
            <div class="status-item">
              <span class="label">類科：</span>
              <span class="value">{{ PaperDetail.displayCategory }}</span>
            </div>
            <div class="status-item">
              <span class="label">姓名：</span>
              <span class="value">{{ PaperDetail.studentName }}</span>
            </div>
            <div class="status-item">
              <span class="label">科目：</span>
              <span class="value">{{ PaperDetail.subjectName }}</span>
            </div>
          </div>

          <div class="status-divider"></div>

          <div class="status-row">
            <div class="status-item">
              <span class="label">題數：</span>
              <span class="value">{{ Questions.length }} 題</span>
            </div>
            <div class="status-item">
              <span class="label">已作答題數：</span>
              <span class="value">{{ AnsweredCount }} 題</span>
            </div>
            <div class="status-item" :class="{ 'warning': UnansweredCount > 0 }">
              <span class="label">未作答：</span>
              <span class="value">{{ UnansweredCount }} 題</span>
            </div>
            <div class="status-item">
              <span class="label">作答時長：</span>
              <span class="value" :class="{ 'text-red': IsOvertime }">
                {{ FormattedDuration }}
                <span v-if="IsOvertime" class="overtime-tag">(超時)</span>
              </span>
            </div>
            <div
              v-if="PaperDetail.gradingDate"
              class="status-item"
            >
              <span class="label">交卷時間：</span>
              <span class="value">
                {{ PaperDetail.gradingDate | toTaipei | moment('YYYY-MM-DD HH:mm:ss') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- General Feedback -->
    <div v-if="PaperDetail.gradingStatus === 3" class="row mb-3">
      <div class="col-12">
        <div class="general-feedback">
          <div class="feedback-header">
            <span>總評語</span>
          </div>
          <div class="feedback-content">
            {{ PaperDetail.teacherComment }}
          </div>
        </div>
      </div>
    </div>

    <!-- Question Tabs -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="question-tabs">
          <button
            v-for="(question, index) in Questions"
            :key="question.essayQuestionsId"
            class="tab-btn"
            :class="{ active: CurrentQuestionIndex === index }"
            @click="LoadQuestion(index)"
          >
            <span class="tab-label">第 {{ index + 1 }} 題</span>
            <span class="tab-score">
              ({{ question.studentScore || '--' }}/{{ question.essayQuestionScore || '--' }})
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Split View Container -->
    <div class="row">
      <div class="col-12">
        <div class="split-view-container">
          <!-- Left Panel: Answer -->
          <div class="left-panel">
            <!-- Question Info -->
            <div class="question-info-card">
              <div class="info-header">
                <span class="question-title">
                  題目 {{ CurrentQuestionIndex + 1 }}
                </span>
                <span class="question-score-badge">
                  得分：
                  <span class="score-red">
                    {{  CurrentQuestion.studentScore || '--' }}
                  </span>
                  /{{ CurrentQuestion.essayQuestionScore || '--' }}
                </span>
              </div>
              <div
                class="info-body"
                v-html="CurrentQuestion.essayQuestionContent"
              ></div>
            </div>

            <!-- Editor (Read-Only) -->
            <div class="editor-container">
              <Ckeditor
                v-if="IsDataLoaded"
                ref="ckeditorRef"
                :key="CurrentQuestion.essayQuestionsId"
                :initial-html="EditorContent"
                :initial-mode="'readonly'"
                :initial-annotations="CurrentAnnotations"
                :max-word-count="CurrentQuestionWordLimit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Back Button -->
    <div class="row mt-4">
      <div class="col-12">
        <div class="text-center">
          <button @click="GoBack" class="btn btn-secondary">
            <i class="fa fa-chevron-left"></i> 返回列表
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Ckeditor from '@/components/Ckeditor4.vue';
import { GetStudentEssayPaperDetail } from '@/api/EssayPapersApi';

export default {
  name: 'EssayReview',
  components: {
    Ckeditor,
  },
  data() {
    return {
      CourseId: null,
      PaperId: null,
      PaperDetail: {},
      Questions: [],
      CurrentQuestionIndex: 0,
      EditorContent: '',
      CurrentAnnotations: [],
      IsDataLoaded: false,
    };
  },
  computed: {
    CurrentQuestion() {
      return this.Questions[this.CurrentQuestionIndex] || {};
    },
    CurrentQuestionWordLimit() {
      return this.CurrentQuestion.essayQuestionWordLimit || 2500;
    },
    FullScore() {
      return this.Questions.reduce(
        (sum, q) => sum + (q.essayQuestionScore || 0),
        0,
      );
    },
    AnsweredCount() {
      let count = 0;
      this.Questions.forEach((q) => {
        const answer = q.answerContent;
        if (answer && answer.trim() !== '') {
          count += 1;
        }
      });
      return count;
    },
    UnansweredCount() {
      return this.Questions.length - this.AnsweredCount;
    },
    FormattedDuration() {
      const seconds = this.PaperDetail.totalDurationSeconds || 0;
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    IsOvertime() {
      const totalSeconds = this.PaperDetail.totalDurationSeconds || 0;
      const allowedSeconds = this.PaperDetail.allowedDurationSeconds || 0;
      return allowedSeconds > 0 && totalSeconds > allowedSeconds;
    },
  },
  async created() {
    this.CourseId = this.$route.params.courseId;
    this.PaperId = this.$route.params.paperId;
    await this.LoadPaperDetail();
  },
  methods: {
    async LoadPaperDetail() {
      try {
        const res = await GetStudentEssayPaperDetail({
          essayPapersId: parseInt(this.PaperId, 10),
          courseId: parseInt(this.CourseId, 10),
        });

        if (res.data.statusCode === 0) {
          const { mainInfo, questions } = res.data.data;
          this.PaperDetail = mainInfo;
          this.Questions = questions || [];
          this.LoadQuestion(0);
        }
      } catch (error) {
        this.$message.error('載入批改資料失敗');
      }
    },
    LoadQuestion(index) {
      this.CurrentQuestionIndex = index;
      const question = this.Questions[index];
      this.EditorContent = question.answerContent || '';
      this.CurrentAnnotations = this.ParseAnnotations(question.annotations);

      this.IsDataLoaded = false;
      this.$nextTick(() => {
        this.IsDataLoaded = true;
      });
    },
    ParseAnnotations(annotations) {
      if (!annotations) return [];
      try {
        return typeof annotations === 'string'
          ? JSON.parse(annotations)
          : annotations;
      } catch (error) {
        return [];
      }
    },
    GoBack() {
      this.$router.push({
        name: 'essayPapersList',
        params: {
          courseId: this.CourseId,
        },
      });
    },
  },
};
</script>

<style scoped>
/* ========== Header ========== */
.exam-header {
  padding: 16px 20px;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.bg-green {
  background: #f0fdf4;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.exam-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.header-right {
  display: flex;
  gap: 8px;
}

.score-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
}

.score-badge .label {
  font-size: 14px;
  color: #155724;
  font-weight: 500;
}

.score-badge .score {
  font-size: 20px;
  font-weight: 700;
  color: #28a745;
}

.score-badge .divider {
  font-size: 16px;
  color: #6c757d;
}

.score-badge .full-score {
  font-size: 16px;
  color: #6c757d;
}

/* ========== Status Bar ========== */
.status-bar {
  background: #fff;
  padding: 12px 20px;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.status-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
  padding: 4px 0;
}

.status-divider {
  width: 100%;
  height: 1px;
  background: #f3f4f4;
  margin: 4px 0;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.status-item i {
  color: #586069;
}

.status-icon-red {
  color: #dc3545 !important;
}

.label {
  color: #586069;
}

.value {
  font-weight: 600;
  color: #212529;
}

.text-red {
  color: #dc3545 !important;
}

.overtime-tag {
  color: #dc3545;
  font-weight: 600;
  margin-left: 4px;
}

.status-item.warning {
  background: #fff3cd;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #ffc107;
}

.status-item.warning .label,
.status-item.warning .value {
  color: #856404;
}

/* ========== General Feedback ========== */
.general-feedback {
  background: #f6f8fa;
  border: 3px solid #0095a3;
  border-radius: 6px;
  padding: 16px;
}

.feedback-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
}

.feedback-content {
  font-size: 16px;
  line-height: 1.7;
  white-space: pre-wrap;
}

/* ========== Question Tabs ========== */
.question-tabs {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #f6f8fa;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  flex-wrap: wrap;
}

.tab-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 16px;
  border: 1px solid #d1d5da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: #f3f4f6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-btn.active {
  background: #0366d6;
  color: #fff;
  border-color: #0366d6;
}

.tab-label {
  font-size: 14px;
  font-weight: 500;
}

.tab-score {
  font-size: 12px;
  margin-top: 4px;
  opacity: 0.9;
}

/* ========== Split View ========== */
.split-view-container {
  display: flex;
  gap: 0;
  min-height: 600px;
  background: #fff;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  overflow: hidden;
}

.left-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 20px;
  min-width: 0;
}

.question-info-card {
  background: #f8f9fa;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #e1e4e8;
  margin-bottom: 12px;
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: #0096a4;
}

.question-score-badge {
  font-size: 14px;
  font-weight: 600;
}

.score-red {
  color: #dc3545;
  font-size: 18px;
}

.info-body {
  font-size: 14px;
  line-height: 1.7;
  color: #212529;
}

.editor-container {
  flex: 1;
  min-height: 400px;
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .exam-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-left {
    width: 100%;
  }

  .header-right {
    width: 100%;
  }

  .score-badge {
    width: 100%;
    justify-content: center;
  }

  .split-view-container {
    flex-direction: column;
  }

  .left-panel {
    padding: 16px;
  }

  .question-tabs {
    gap: 6px;
    padding: 10px 12px;
  }

  .tab-btn {
    padding: 8px 12px;
  }

  .tab-label {
    font-size: 13px;
  }

  .tab-score {
    font-size: 11px;
  }
}
</style>
